<template>
  <div class="w-full min-h-screen flex flex-col">
    <MainNav />

    <section class="w-full relative bg-black pt-24">
      <!-- Header -->
      <div class="container mx-auto px-4 pt-12 pb-6">
        <h1 class="text-white font-maax text-4xl lg:text-6xl font-bold uppercase text-center mb-4">
          The Rackhouse
        </h1>
        <p class="text-white font-maax text-xl text-center max-w-3xl mx-auto">
          {{ loading ? 'Loading...' : `${filledCasksCount} Casks of Aging Excellence` }}
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="container mx-auto px-4 pb-12 text-center py-20">
        <div class="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-gold"></div>
        <p class="mt-4 text-white text-xl">Loading casks...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="container mx-auto px-4 pb-12">
        <div class="p-6 bg-red-900 border border-red-700 rounded-lg">
          <p class="text-white">{{ error }}</p>
        </div>
      </div>

      <!-- Introduction Text -->
      <div v-if="!loading && !error" class="container mx-auto px-4 pb-6">
        <div class="space-y-4 text-gray-300 leading-relaxed text-center max-w-5xl mx-auto">
          <p>
            The Rackhouse is where our rums lives breathing ocean air as cyclones pass and time does its slow work. Here, every cask is a chapter in the story of Vanuatu: cane cut at sunrise, spirit laid down in wood, waiting for the right moment to be shared.
          </p>

          <p>
            With The Rackhouse, we believe we're the first distillery in the world to open its barrel inventory fully to everyone, in real time. This is our cellar, laid bare: searchable, traceable, and alive. You can follow a favourite batch back to the exact casks it came from, explore what's aging now, or simply wander through the future of 83 Islands one cask at a time.
          </p>

          <p>
            Some older casks are still making the journey from handwritten logbooks into this new system, so you may notice a few gaps. The colour shown for each barrel reflects its appearance based on industry-standard evaluation, but not every cask has been graded in our new electronic records yet. We’ll keep updating these colours on a regular basis as we sample, taste, and track the evolution of our inventory.
          </p>
        </div>
      </div>

      <!-- Filter and Sort Panel -->
      <div v-if="!loading && !error" class="container mx-auto px-4 pb-6">
        <div class="bg-black text-white p-6 rounded-lg mb-6">
          <div class="flex justify-between items-center cursor-pointer" @click="showFilters = !showFilters">
            <div class="flex items-center gap-3">
              <h3 class="text-lg font-semibold uppercase">Filters & Sorting</h3>
              <span v-if="filters.batch" class="text-sm bg-gold text-black px-2 py-0.5 rounded">
                Batch: {{ filters.batch }}
              </span>
            </div>
            <span class="text-white text-xl">{{ showFilters ? '−' : '+' }}</span>
          </div>

          <Transition name="slide-down">
            <div v-if="showFilters">
              <div class="flex justify-end mt-4 mb-2">
                <button
                  @click.stop="resetFilters"
                  class="text-sm text-black bg-white px-3 py-1 rounded hover:bg-gray-200 transition-colors"
                >
                  Reset All
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <!-- Status Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">Status</label>
              <div class="grid grid-cols-2 gap-2">
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input v-model="filters.status" type="checkbox" value="filled" class="rounded border-gray-600 bg-gray-800 text-gold focus:ring-gold">
                  <span class="text-sm">Filled</span>
                </label>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input v-model="filters.status" type="checkbox" value="empty" class="rounded border-gray-600 bg-gray-800 text-gold focus:ring-gold">
                  <span class="text-sm">Empty</span>
                </label>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input v-model="filters.status" type="checkbox" value="broken" class="rounded border-gray-600 bg-gray-800 text-gold focus:ring-gold">
                  <span class="text-sm">Broken</span>
                </label>
              </div>
            </div>

            <!-- Age Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">Minimum Age (years)</label>
              <div class="space-y-3">
                <input
                  v-model.number="filters.ageMin"
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  class="w-full accent-gold"
                >
                <div class="flex justify-between text-xs text-gray-400">
                  <span>0y</span>
                  <span class="text-white font-semibold">{{ filters.ageMin }}y+</span>
                  <span>10y</span>
                </div>
              </div>
            </div>

            <!-- ABV Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">ABV (%)</label>
              <div class="space-y-3">
                <input
                  v-model.number="filters.abv"
                  type="range"
                  min="0"
                  max="100"
                  step="0.5"
                  class="w-full accent-gold"
                >
                <div class="flex justify-between text-xs text-gray-400">
                  <span>0%</span>
                  <span class="text-white font-semibold">{{ filters.abv }}%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>

            <!-- Sorting -->
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">Sort By</label>
              <select
                v-model="sortBy"
                class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm focus:ring-gold focus:border-gold"
              >
                <option value="">Cask Number</option>
                <option value="age">Age (Oldest First)</option>
                <option value="age-desc">Age (Newest First)</option>
                <option value="abv">ABV (Low to High)</option>
                <option value="abv-desc">ABV (High to Low)</option>
                <option value="lal">LAL (Low to High)</option>
                <option value="lal-desc">LAL (High to Low)</option>
                <option value="entropy">Entropy Index</option>
              </select>
            </div>

            <!-- Barrel Type Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">Cask Types</label>
              <select
                v-model="filters.barrelType"
                class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm focus:ring-gold focus:border-gold"
              >
                <option value="">All Types</option>
                <option v-for="type in availableBarrelTypes" :key="type" :value="type">
                  {{ type }}
                </option>
              </select>
            </div>

            <!-- Content Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">Content</label>
              <select
                v-model="filters.content"
                class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm focus:ring-gold focus:border-gold"
              >
                <option value="">All Content</option>
                <option v-for="content in availableContentTypes" :key="content" :value="content">
                  {{ content }}
                </option>
              </select>
            </div>

            <!-- Batch Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-400 mb-2">Batch</label>
              <select
                v-model="filters.batch"
                class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-sm focus:ring-gold focus:border-gold"
              >
                <option value="">All Batches</option>
                <option v-for="batch in availableBatches" :key="batch" :value="batch">
                  {{ batch }}
                </option>
              </select>
            </div>
          </div>

              <!-- Results Count -->
              <div class="mt-4 pt-4 border-t border-gray-700 text-sm text-gray-400">
                Showing <span class="text-white font-semibold">{{ filteredBarrels.length }}</span> of {{ barrels.length }} casks
              </div>
            </div>
          </Transition>
        </div>
      </div>

      <!-- Barrel Grid -->
      <div class="container mx-auto px-4 pb-12 relative">
        <div class="grid grid-cols-custom gap-2 barrel-grid">
          <template v-for="(barrel, index) in filteredBarrels" :key="barrel.number">
            <!-- Barrel Item -->
            <div
              :ref="el => setBarrelRef(barrel.number, el)"
              :data-barrel="barrel.number"
              class="relative group cursor-pointer aspect-square flex items-center justify-center transition-all duration-300"
              :class="{ 'barrel-selected': selectedBarrel?.number === barrel.number }"
              :title="`Barrel ${barrel.number} - ${getColorName(barrel.color)}`"
              @click="handleBarrelClick(barrel)"
            >
              <!-- Barrel SVG with dynamic colors -->
              <div
                v-if="barrelSvgContent"
                v-html="getBarrelSvg(barrel)"
                class="w-full h-full barrel-svg transition-transform duration-200 group-hover:scale-110"
              ></div>
              <!-- Fallback while loading -->
              <img
                v-else
                src="/image/83Barrel.svg"
                alt="Barrel"
                class="w-full h-full object-contain transition-transform duration-200 group-hover:scale-110"
              />
              <!-- Barrel Number Overlay -->
              <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pt-2">
                <span class="text-black font-bold text-lg sm:text-xl md:text-2xl opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-200">
                  {{ barrel.number }}
                </span>
                <span v-if="barrel.details?.fillingDate" class="text-black text-xs sm:text-sm font-semibold opacity-75 group-hover:opacity-100 transition-all duration-200">
                  {{ getBarrelAge(barrel.details.fillingDate) }}
                </span>
              </div>
              <!-- Batch Association Badge -->
              <div v-if="barrel.details?.blends && barrel.details.blends.length > 0" class="absolute bottom-1 right-1 w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full flex items-center justify-center shadow-lg pointer-events-none">
                <span class="text-black font-bold text-xs sm:text-sm">B</span>
              </div>
            </div>

            <!-- Inline Barrel Details Panel - appears after the row -->
            <Transition name="slide-down">
              <div
                v-if="shouldShowDetailsAfter(index)"
                class="bg-black text-white p-6 my-2 rounded-lg barrel-details-panel"
                style="grid-column: 1 / -1;"
              >
                <!-- Close button -->
                <button
                  @click="closeBarrelDetails"
                  class="absolute top-4 right-4 text-sm text-black bg-white px-3 py-1 rounded hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>

                <!-- Header -->
                <h2 class="text-2xl font-bold mb-6">Cask #{{ selectedBarrel.number }}</h2>

                <!-- One panel per fill, oldest first, each with the same detail:
                     a cask refilled after its first blend shows the 1st fill
                     (and the batches it went into) above the one ageing now.
                     Earlier fills are measured filling → emptying. -->
                <div
                  v-for="(view, vi) in fillViews"
                  :key="view.key"
                  :class="vi > 0 ? 'mt-6 pt-6 border-t-2 border-gray-600' : ''"
                >
                  <p v-if="view.title" class="text-gold font-semibold uppercase text-sm mb-3">
                    {{ view.title }}
                    <span v-if="view.current" class="text-gray-400 font-normal normal-case ml-2">current · ageing</span>
                    <span v-else-if="view.endDate" class="text-gray-400 font-normal normal-case ml-2">emptied {{ formatDate(view.endDate) }}</span>
                  </p>
                  <!-- Compact Grid Layout -->
                  <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
                    <!-- 1. Status -->
                    <div>
                      <p class="text-gray-400 text-xs uppercase">Status</p>
                      <p class="font-semibold capitalize">{{ view.status }}</p>
                    </div>

                    <!-- 2. Filling Date (hidden for broken barrels) -->
                    <div v-if="view.details?.fillingDate && view.status !== 'broken'">
                      <p class="text-gray-400 text-xs uppercase">Filled</p>
                      <p class="font-semibold">{{ formatDate(view.details.fillingDate) }}</p>
                    </div>

                    <!-- 3. Age (hidden for broken barrels) -->
                    <div v-if="view.details?.fillingDate && view.status !== 'broken'">
                      <p class="text-gray-400 text-xs uppercase">Age</p>
                      <p class="font-semibold">{{ getBarrelAge(view.details.fillingDate, view.endDate) }}</p>
                    </div>

                    <!-- 4. Content -->
                    <div>
                      <p class="text-gray-400 text-xs uppercase">Content</p>
                      <p class="font-semibold">{{ getDisplayContent(view.details?.content) }}</p>
                    </div>

                    <!-- 5. Barrel Type -->
                    <div v-if="view.details?.barrelType">
                      <p class="text-gray-400 text-xs uppercase">Type</p>
                      <p class="font-semibold">{{ view.details.barrelType }}</p>
                    </div>

                    <!-- 5b. Fill Number -->
                    <div v-if="view.details?.fillNumber">
                      <p class="text-gray-400 text-xs uppercase">Fill</p>
                      <p class="font-semibold">{{ view.details.fillNumber }}</p>
                    </div>

                    <!-- 6. ABV -->
                    <div v-if="view.details?.abv">
                      <p class="text-gray-400 text-xs uppercase">ABV</p>
                      <p class="font-semibold">{{ view.details.abv }}%</p>
                    </div>

                    <!-- 7. LAL (hidden for broken barrels) -->
                    <div v-if="view.details?.lal && view.status !== 'broken'">
                      <p class="text-gray-400 text-xs uppercase">LAL</p>
                      <p class="font-semibold">{{ view.details.lal }}</p>
                    </div>

                    <!-- 8. Expected Angel Share (hidden for broken barrels) -->
                    <div v-if="view.details?.fillingDate && view.status !== 'broken'">
                      <p class="text-gray-400 text-xs uppercase">Angel Share</p>
                      <div class="flex items-center gap-1">
                        <p class="font-semibold">{{ getExpectedAngelShare(view.details.fillingDate, view.endDate) }}%</p>
                        <div class="relative group">
                          <span class="inline-flex items-center justify-center w-3 h-3 rounded-full border border-gray-400 text-gray-400 cursor-help text-xs font-serif italic">i</span>
                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            Estimate based on 7% annual loss
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 9. LAL Remaining (hidden for broken barrels) -->
                    <div v-if="view.details?.lal && view.details?.fillingDate && view.status !== 'broken'">
                      <p class="text-gray-400 text-xs uppercase">LAL Remaining</p>
                      <div class="flex items-center gap-1">
                        <p class="font-semibold">{{ getLALRemaining(view.details.lal, view.details.fillingDate, view.endDate) }}L</p>
                        <div class="relative group">
                          <span class="inline-flex items-center justify-center w-3 h-3 rounded-full border border-gray-400 text-gray-400 cursor-help text-xs font-serif italic">i</span>
                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            Estimated value
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 10. Approximate Bottles at 46% ABV (hidden for broken barrels) -->
                    <div v-if="view.details?.lal && view.details?.fillingDate && view.status !== 'broken'">
                      <p class="text-gray-400 text-xs uppercase">Bottles (46% ABV)</p>
                      <div class="flex items-center gap-1">
                        <p class="font-semibold">{{ getApproxBottles(view.details.lal, view.details.fillingDate, view.endDate) }}</p>
                        <div class="relative group">
                          <span class="inline-flex items-center justify-center w-3 h-3 rounded-full border border-gray-400 text-gray-400 cursor-help text-xs font-serif italic">i</span>
                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            Estimated 700ml bottles at 46% ABV
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 11. Yeast -->
                    <div>
                      <p class="text-gray-400 text-xs uppercase">Yeast</p>
                      <p class="font-semibold">TBD</p>
                    </div>

                    <!-- Color (hidden for broken barrels) -->
                    <div v-if="view.status !== 'broken'">
                      <p class="text-gray-400 text-xs uppercase">Color</p>
                      <div class="flex items-center gap-1">
                        <p class="font-semibold">{{ view.color === null && !view.current ? 'Not evaluated' : getColorName(view.color) }}</p>
                        <div
                          v-if="view.color === medianColor && view.status === 'filled'"
                          class="relative group"
                        >
                          <span class="inline-flex items-center justify-center w-3 h-3 rounded-full border border-gray-400 text-gray-400 cursor-help text-xs font-serif italic">i</span>
                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            Median color, not evaluated recently
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Aroma -->
                    <div v-if="view.details?.evaluation?.aromaIntensity">
                      <p class="text-gray-400 text-xs uppercase">Aroma</p>
                      <p class="font-semibold capitalize">{{ view.details.evaluation.aromaIntensity }}</p>
                    </div>

                    <!-- Flavor -->
                    <div v-if="view.details?.evaluation?.flavorIntensity">
                      <p class="text-gray-400 text-xs uppercase">Flavor</p>
                      <p class="font-semibold capitalize">{{ view.details.evaluation.flavorIntensity }}</p>
                    </div>
                  </div>

                  <!-- Rarity and Triple Force (on same line) -->
                  <div v-if="view.details?.tripleForce?.rarity?.positionStatement || view.details?.tripleForce?.forces" class="mt-4 pt-4 border-t border-gray-700">
                    <!-- Entropy Index Title -->
                    <div class="flex items-center gap-2 mb-1">
                      <h3 class="text-sm font-semibold text-white uppercase">Entropy Index</h3>
                      <a
                        href="/entropy-index"
                        target="_blank"
                        class="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-400 text-gray-400 hover:border-gold hover:text-gold transition-colors"
                        title="Learn more about the Entropy Index"
                      >
                        <span class="text-xs font-serif italic">i</span>
                      </a>
                    </div>

                    <div class="flex flex-wrap items-center gap-4 text-sm">
                      <!-- Rarity -->
                      <div v-if="view.details?.tripleForce?.rarity?.positionStatement">
                        <span class="text-gold font-semibold">{{ view.details.tripleForce.rarity.category }}</span>
                        <span class="text-gray-300"> - {{ view.details.tripleForce.rarity.positionStatement }}</span>
                      </div>

                      <!-- Triple Force Data -->
                      <div v-if="view.details?.tripleForce?.forces" class="flex gap-4 ml-auto">
                        <div v-if="view.details.tripleForce.forces.seismic" class="text-center">
                          <p class="text-gray-400 text-xs uppercase">Seismic</p>
                          <p class="font-semibold">{{ view.details.tripleForce.forces.seismic.score?.toFixed(1) }}</p>
                          <p class="text-xs text-gray-400">{{ view.details.tripleForce.forces.seismic.assessment }}</p>
                        </div>
                        <div v-if="view.details.tripleForce.forces.pressure" class="text-center">
                          <p class="text-gray-400 text-xs uppercase">Pressure</p>
                          <p class="font-semibold">{{ view.details.tripleForce.forces.pressure.score?.toFixed(1) }}</p>
                          <p class="text-xs text-gray-400">{{ view.details.tripleForce.forces.pressure.assessment }}</p>
                        </div>
                        <div v-if="view.details.tripleForce.forces.wind" class="text-center">
                          <p class="text-gray-400 text-xs uppercase">Wind</p>
                          <p class="font-semibold">{{ view.details.tripleForce.forces.wind.score?.toFixed(1) }}</p>
                          <p class="text-xs text-gray-400">{{ view.details.tripleForce.forces.wind.assessment }}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- The batches this fill went into -->
                  <div v-if="view.batches" class="mt-4 pt-4 border-t border-gray-700">
                    <p class="text-gray-400 text-xs uppercase mb-2">{{ view.batches.length > 1 ? 'Batches' : 'Batch' }}</p>
                    <div v-if="view.batches.length" class="flex flex-col gap-1.5">
                      <div
                        v-for="b in view.batches"
                        :key="b.batchNumber"
                        class="bg-gray-800 px-3 py-1.5 rounded text-sm flex items-baseline gap-3 w-fit"
                      >
                        <span class="font-semibold">{{ b.batchNumber }}</span>
                        <span v-if="b.blendDate" class="text-gray-400">{{ formatBlendDate(b.blendDate) }}</span>
                      </div>
                    </div>
                    <p v-else class="text-sm text-gray-400">{{ view.current ? 'Still ageing' : 'Not in a batch' }}</p>
                  </div>
                </div>

                <!-- A cask on its only fill: just the batches it went into. -->
                <div v-if="fillViews.length === 1 && selectedBarrel.details?.blends && selectedBarrel.details.blends.length > 0" class="mt-4 pt-4 border-t border-gray-700">
                  <p class="text-gray-400 text-xs uppercase mb-2">Batches</p>
                  <div class="flex flex-col gap-1.5">
                    <div
                      v-for="(blend, index) in selectedBarrel.details.blends"
                      :key="index"
                      class="bg-gray-800 px-3 py-1.5 rounded text-sm flex items-baseline gap-3 w-fit"
                    >
                      <span class="font-semibold">{{ blend.batchNumber }}</span>
                      <span v-if="blend.blendDate" class="text-gray-400">{{ formatBlendDate(blend.blendDate) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </template>
        </div>
      </div>

      <!-- Last Updated Timestamp -->
      <div v-if="!loading && !error && lastUpdated" class="container mx-auto px-4 pb-12 text-center">
        <p class="text-gray-400 text-sm">
          Last updated: <span class="text-white">{{ formatLastUpdated(lastUpdated) }}</span>
        </p>
      </div>

    </section>

    <Footer class="relative" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MainNav from '../components/MainNav.vue'
import Footer from '../components/Footer.vue'

const route = useRoute()
const router = useRouter()

// Google Analytics tracking helper
const trackEvent = (eventName, eventParams = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams)
  }
}

// Color mapping from numeric values (0.0 to 2.0) to hex colors
const colorMap = {
  0.0: { name: 'Gin Clear', hex: '#ffffff' },
  0.1: { name: 'White Wine', hex: '#fffddc' },
  0.2: { name: 'Pale Straw', hex: '#feee97' },
  0.3: { name: 'Pale Gold', hex: '#fbea78' },
  0.4: { name: 'Corn', hex: '#fae065' },
  0.5: { name: 'Yellow Gold', hex: '#f7db54' },
  0.6: { name: 'Old Gold', hex: '#f8d849' },
  0.7: { name: 'Amber', hex: '#f7cf49' },
  0.8: { name: 'Deep Gold', hex: '#f6ca4d' },
  0.9: { name: 'Light Sherry', hex: '#f6c23b' },
  1.0: { name: 'Deep Copper', hex: '#f5be30' },
  1.1: { name: 'Burnished', hex: '#eaad07' },
  1.2: { name: 'Oloroso Sherry', hex: '#e79e14' },
  1.3: { name: 'Ripe Muscat', hex: '#e59502' },
  1.4: { name: 'Tawny', hex: '#dc7415' },
  1.5: { name: 'Auburn', hex: '#e06529' },
  1.6: { name: 'Mahogany', hex: '#cc4f2f' },
  1.7: { name: 'Burnt Umber', hex: '#af3029' },
  1.8: { name: 'Old Oak', hex: '#a1221b' },
  1.9: { name: 'Brown Sherry', hex: '#722d1d' },
  2.0: { name: 'Molasses', hex: '#421b0a' }
}

const barrelSvgContent = ref('')

// Store refs to barrel elements for scrolling
const barrelRefs = ref({})
const setBarrelRef = (barrelNumber, el) => {
  if (el) {
    barrelRefs.value[barrelNumber] = el
  }
}

// Scroll to a specific barrel and select it
const scrollToBarrel = async (barrelNumber) => {
  await nextTick()

  // Find the barrel in the data
  const barrel = barrels.value.find(b => b.number === barrelNumber)
  if (!barrel) {
    console.warn(`Barrel #${barrelNumber} not found`)
    return false
  }

  // Check if barrel is in filtered results
  const isInFiltered = filteredBarrels.value.some(b => b.number === barrelNumber)
  if (!isInFiltered) {
    // Reset filters to show all barrels
    resetFilters()
    await nextTick()
  }

  // Wait for DOM to update
  await nextTick()

  // Find the barrel element
  const barrelEl = barrelRefs.value[barrelNumber] || document.querySelector(`[data-barrel="${barrelNumber}"]`)

  if (barrelEl) {
    // Scroll to the barrel with some offset for the header
    const headerOffset = 150
    const elementPosition = barrelEl.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })

    // Select the barrel to show details
    selectedBarrel.value = barrel

    // Track the direct link usage
    trackEvent('rackhouse_direct_link', {
      barrel_number: barrelNumber
    })

    return true
  }

  return false
}

// Function to get hex color from numeric value
const getColorHex = (numericValue) => {
  if (numericValue === null) return '#ce9e7a' // Natural oak color
  if (numericValue === -0.5) return '#ffb3b3' // Pale red for broken barrels
  // Round to nearest 0.1
  const rounded = Math.round(numericValue * 10) / 10
  return colorMap[rounded]?.hex || '#ce9e7a' // fallback color
}

// Function to get color name from numeric value
const getColorName = (numericValue) => {
  if (numericValue === null) return 'Natural Oak'
  if (numericValue === -0.5) return 'Broken (Pale Red)'
  const rounded = Math.round(numericValue * 10) / 10
  return colorMap[rounded]?.name || 'Unknown'
}

// Function to darken color by 20% brightness
const getDarkerColor = (numericValue) => {
  const hexColor = getColorHex(numericValue)

  // Remove # if present
  const hex = hexColor.replace('#', '')

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Reduce brightness by 20%
  const darkenFactor = 0.8
  const newR = Math.round(r * darkenFactor)
  const newG = Math.round(g * darkenFactor)
  const newB = Math.round(b * darkenFactor)

  // Convert back to hex
  const toHex = (value) => {
    const hex = value.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`
}

// Get barrel SVG content with dynamic colors
const getBarrelSvg = (barrel) => {
  if (!barrelSvgContent.value) return ''

  // Handle null color (empty barrels) - use default SVG color
  if (barrel.color === null) {
    // For empty barrels, still need to convert to inline styles
    let svg = barrelSvgContent.value
    svg = svg.replace(/<style>.*?<\/style>/s, '')
    svg = svg.replace(/id="Layer_1"/g, `id="barrel_${barrel.number}"`)
    svg = svg.replace(/class="st0"/g, 'style="fill:#010101"')
    svg = svg.replace(/class="st1"/g, 'style="fill:#99745d"')
    svg = svg.replace(/class="st2"/g, 'style="fill:#ce9e7a"')
    svg = svg.replace(/class="st3"/g, 'style="fill:#413a36"')
    return svg
  }

  const st2Color = getColorHex(barrel.color)      // Main barrel color
  const st1Color = getDarkerColor(barrel.color)   // 20% darker than st2 (shadow area)

  // Debug logging for variety - show different color values
  if (barrel.number <= 10 || barrel.color !== 1.4) {
    console.log(`Cask #${barrel.number}: color value=${barrel.color}, st2 (main)=${st2Color}, st1 (shadow)=${st1Color}`)
  }

  // Convert CSS classes to inline styles to avoid conflicts between barrels
  let svg = barrelSvgContent.value

  // Remove the <style> block entirely
  svg = svg.replace(/<style>.*?<\/style>/s, '')

  // Make SVG ID unique per barrel to avoid conflicts
  svg = svg.replace(/id="Layer_1"/g, `id="barrel_${barrel.number}"`)

  // Replace all class attributes with inline styles
  svg = svg.replace(/class="st0"/g, 'style="fill:#010101"')  // Keep st0 as dark outline
  svg = svg.replace(/class="st1"/g, `style="fill:${st1Color}"`)  // st1 is the shadow - 20% darker than main
  svg = svg.replace(/class="st2"/g, `style="fill:${st2Color}"`)  // st2 is the main barrel color
  svg = svg.replace(/class="st3"/g, 'style="fill:#413a36"')  // Keep st3 as default dark gray

  return svg
}

// Barrels data from database
const barrels = ref([])
const loading = ref(true)
const error = ref(null)
const medianColor = ref(1.0)
const lastUpdated = ref(null)
const selectedBarrel = ref(null)
const windowWidth = ref(window.innerWidth)

// Filter and sort state
const filters = ref({
  status: ['filled', 'empty', 'broken'], // All selected by default
  ageMin: 0,
  abv: 0,
  barrelType: '',
  content: '',
  batch: ''
})

const showFilters = ref(false)

const sortBy = ref('')

// Helper function to get barrel age in years
const getBarrelAgeYears = (barrel) => {
  if (!barrel.details?.fillingDate) return null
  const filled = new Date(barrel.details.fillingDate)
  const now = new Date()
  const diffTime = Math.abs(now - filled)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays / 365
}

// Computed filtered and sorted barrels
const filteredBarrels = computed(() => {
  let result = [...barrels.value]

  // Filter by status
  if (filters.value.status.length > 0) {
    result = result.filter(barrel => filters.value.status.includes(barrel.status))
  }

  // Filter by minimum age (only if slider is > 0)
  if (filters.value.ageMin > 0) {
    result = result.filter(barrel => {
      const age = getBarrelAgeYears(barrel)
      return age !== null && age >= filters.value.ageMin
    })
  }

  // Filter by ABV (show barrels with ABV >= slider value)
  if (filters.value.abv > 0) {
    result = result.filter(barrel => {
      return barrel.details?.abv && barrel.details.abv >= filters.value.abv
    })
  }

  // Filter by barrel type
  if (filters.value.barrelType) {
    result = result.filter(barrel => barrel.details?.barrelType === filters.value.barrelType)
  }

  // Filter by content (already transformed by server)
  if (filters.value.content) {
    result = result.filter(barrel => barrel.details?.content === filters.value.content)
  }

  // Filter by batch number
  if (filters.value.batch) {
    result = result.filter(barrel => {
      return barrel.details?.blends?.some(blend => blend.batchNumber === filters.value.batch)
    })
  }

  // Sorting
  if (sortBy.value === 'age') {
    result.sort((a, b) => {
      const ageA = getBarrelAgeYears(a) || 0
      const ageB = getBarrelAgeYears(b) || 0
      return ageB - ageA // Oldest first
    })
  } else if (sortBy.value === 'age-desc') {
    result.sort((a, b) => {
      const ageA = getBarrelAgeYears(a) || 0
      const ageB = getBarrelAgeYears(b) || 0
      return ageA - ageB // Newest first
    })
  } else if (sortBy.value === 'abv') {
    result.sort((a, b) => (a.details?.abv || 0) - (b.details?.abv || 0))
  } else if (sortBy.value === 'abv-desc') {
    result.sort((a, b) => (b.details?.abv || 0) - (a.details?.abv || 0))
  } else if (sortBy.value === 'lal') {
    result.sort((a, b) => (a.details?.lal || 0) - (b.details?.lal || 0))
  } else if (sortBy.value === 'lal-desc') {
    result.sort((a, b) => (b.details?.lal || 0) - (a.details?.lal || 0))
  } else if (sortBy.value === 'entropy') {
    result.sort((a, b) => {
      const rarityA = a.details?.tripleForce?.rarity?.rarityScore || 0
      const rarityB = b.details?.tripleForce?.rarity?.rarityScore || 0
      return rarityB - rarityA
    })
  } else {
    // Default: sort by barrel number
    result.sort((a, b) => a.number - b.number)
  }

  return result
})

// Get unique content types for filter dropdown (already transformed by server)
const availableContentTypes = computed(() => {
  const contents = new Set()
  barrels.value.forEach(barrel => {
    if (barrel.details?.content) {
      contents.add(barrel.details.content)
    }
  })
  return Array.from(contents).sort()
})

// Get unique barrel types for filter dropdown
const availableBarrelTypes = computed(() => {
  const types = new Set()
  barrels.value.forEach(barrel => {
    if (barrel.details?.barrelType) {
      types.add(barrel.details.barrelType)
    }
  })
  return Array.from(types).sort()
})

// "1st fill", "2nd fill"… for a cask's batches, oldest first.
const fillLabel = (n) => {
  const v = Number(n)
  const suffix = v % 100 >= 11 && v % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th')
  return `${v}${suffix} fill`
}
const formatBlendDate = (d) => new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })

// The detail panel once per fill. A cask on its only fill keeps the single
// panel; with several, each fill gets the full panel, oldest first. The fill
// ageing now uses the cask's own figures (median colour included).
const fillViews = computed(() => {
  const b = selectedBarrel.value
  if (!b) return []
  const fills = b.details?.fills || []
  if (fills.length <= 1) {
    return [{ key: 'only', title: null, current: b.status === 'filled', status: b.status, color: b.color, endDate: null, details: b.details, batches: null }]
  }
  return fills.map(f => (f.current
    ? { key: f.fill, title: fillLabel(f.fill), current: true, status: b.status, color: b.color, endDate: null, details: b.details, batches: f.batches || [] }
    : {
        key: f.fill,
        title: fillLabel(f.fill),
        current: false,
        status: 'emptied',
        color: f.color,
        endDate: f.emptiedDate || null,
        details: {
          fillingDate: f.fillingDate,
          content: f.content,
          barrelType: f.barrelType,
          fillNumber: f.fillNumber,
          abv: f.abv,
          lal: f.lal,
          evaluation: f.evaluation,
          tripleForce: f.tripleForce
        },
        batches: f.batches || []
      }))
})

// Get unique batch numbers for filter dropdown
const availableBatches = computed(() => {
  const batches = new Set()
  barrels.value.forEach(barrel => {
    if (barrel.details?.blends) {
      barrel.details.blends.forEach(blend => {
        if (blend.batchNumber) {
          batches.add(blend.batchNumber)
        }
      })
    }
  })
  return Array.from(batches).sort()
})

// Count non-empty casks
const filledCasksCount = computed(() => {
  return barrels.value.filter(barrel => barrel.status !== 'empty').length
})

// Reset all filters
const resetFilters = () => {
  filters.value = {
    status: ['filled', 'empty', 'broken'],
    ageMin: 0,
    abv: 0,
    barrelType: '',
    content: '',
    batch: ''
  }
  sortBy.value = ''
}

// Fetch barrels from database
const fetchBarrels = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await fetch('/api/rackhouse-barrels')
    const data = await response.json()

    if (data.success) {
      barrels.value = data.barrels
      medianColor.value = data.medianColor || 1.0
      lastUpdated.value = data.lastUpdated
      console.log(`Loaded ${data.count} barrels with median color: ${medianColor.value}`)
    } else {
      error.value = data.error || 'Failed to load barrels'
    }
  } catch (err) {
    error.value = 'Failed to fetch barrels: ' + err.message
    console.error('Error fetching barrels:', err)
  } finally {
    loading.value = false
  }
}

// Handle barrel click
const handleBarrelClick = (barrel) => {
  // If clicking the same barrel, close it
  if (selectedBarrel.value?.number === barrel.number) {
    selectedBarrel.value = null

    // Update URL to remove barrel number
    router.replace({ name: 'Rackhouse' })

    // Track barrel detail close
    trackEvent('rackhouse_barrel_close', {
      barrel_number: barrel.number
    })
  } else {
    selectedBarrel.value = barrel

    // Update URL with barrel number for shareability
    router.replace({ name: 'RackhouseBarrel', params: { barrelNumber: barrel.number } })

    // Track barrel click
    trackEvent('rackhouse_barrel_click', {
      barrel_number: barrel.number,
      barrel_status: barrel.status,
      barrel_type: barrel.details?.barrelType || 'unknown',
      barrel_content: barrel.details?.content || 'unknown',
      barrel_age_years: getBarrelAgeYears(barrel)?.toFixed(1) || 'unknown',
      barrel_abv: barrel.details?.abv || 'unknown'
    })

    // Debug logging
    const cols = getCurrentColumns.value
    const selectedIndex = barrels.value.findIndex(b => b.number === barrel.number)
    const currentRow = Math.floor(selectedIndex / cols)
    const endOfRow = (currentRow + 1) * cols - 1
    console.log(`Clicked cask #${barrel.number} (index ${selectedIndex})`)
    console.log(`Columns: ${cols}, Row: ${currentRow}, End of row index: ${endOfRow}, End cask #: ${endOfRow + 1}`)
  }
}

// Close barrel details
const closeBarrelDetails = () => {
  if (selectedBarrel.value) {
    // Track barrel detail close via close button
    trackEvent('rackhouse_barrel_close', {
      barrel_number: selectedBarrel.value.number,
      close_method: 'button'
    })
  }
  selectedBarrel.value = null

  // Update URL to remove barrel number
  router.replace({ name: 'Rackhouse' })
}

// Get current grid columns based on breakpoints - max 10 columns
const getCurrentColumns = computed(() => {
  // Access windowWidth to make this reactive
  const width = windowWidth.value

  // Max 10 columns for all screens 640px and wider
  if (width >= 640) return 10
  return 5 // default (mobile)
})

// Update window width on resize
const handleResize = () => {
  windowWidth.value = window.innerWidth // Trigger reactivity
}

// Calculate which barrel index is at the end of the selected barrel's row
const getEndOfRowIndex = computed(() => {
  if (!selectedBarrel.value) return -1

  const selectedIndex = filteredBarrels.value.findIndex(b => b.number === selectedBarrel.value.number)
  if (selectedIndex === -1) return -1

  const cols = getCurrentColumns.value // Access the computed value
  const currentRow = Math.floor(selectedIndex / cols)
  const endOfRow = (currentRow + 1) * cols - 1

  // Return the last barrel in this row (or last barrel if we're on the last row)
  return Math.min(endOfRow, filteredBarrels.value.length - 1)
})

// Check if we should show details after this barrel
const shouldShowDetailsAfter = (index) => {
  return index === getEndOfRowIndex.value
}

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

// Format last updated timestamp
const formatLastUpdated = (dateStr) => {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Display content (already transformed by server)
const getDisplayContent = (content) => {
  return content || 'TBD'
}

// Calculate barrel age from filling date
const getBarrelAge = (fillingDate, endDate = null) => {
  if (!fillingDate) return ''

  const filled = new Date(fillingDate)
  const now = endDate ? new Date(endDate) : new Date()
  const diffTime = Math.abs(now - filled)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  const years = Math.floor(diffDays / 365)
  const months = Math.floor((diffDays % 365) / 30)

  if (years > 0) {
    return `${years}y ${months}m`
  } else if (months > 0) {
    return `${months}m`
  } else {
    return `${diffDays}d`
  }
}

// Calculate expected angel share (7% compounding loss per year)
const getExpectedAngelShare = (fillingDate, endDate = null) => {
  if (!fillingDate) return '0.0'

  const filled = new Date(fillingDate)
  const now = endDate ? new Date(endDate) : new Date()
  const diffTime = Math.abs(now - filled)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  const years = diffDays / 365 // Fractional years for proportional months

  // Compounding: each year loses 7% of remaining volume
  // Remaining = initial * (0.93)^years
  // Loss percentage = (1 - 0.93^years) * 100
  const remainingFraction = Math.pow(0.93, years)
  const lossPercentage = (1 - remainingFraction) * 100

  return lossPercentage.toFixed(1)
}

// Calculate LAL remaining after angel share
const getLALRemaining = (initialLAL, fillingDate, endDate = null) => {
  if (!initialLAL || !fillingDate) return '0.0'

  const filled = new Date(fillingDate)
  const now = endDate ? new Date(endDate) : new Date()
  const diffTime = Math.abs(now - filled)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  const years = diffDays / 365

  // Apply compounding loss: remaining = initial * (0.93)^years
  const remainingFraction = Math.pow(0.93, years)
  const lalRemaining = initialLAL * remainingFraction

  return lalRemaining.toFixed(1)
}

// Calculate approximate bottles at 46% ABV (700ml bottles)
const getApproxBottles = (initialLAL, fillingDate, endDate = null) => {
  if (!initialLAL || !fillingDate) return '0'

  const filled = new Date(fillingDate)
  const now = endDate ? new Date(endDate) : new Date()
  const diffTime = Math.abs(now - filled)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  const years = diffDays / 365

  // Calculate remaining LAL
  const remainingFraction = Math.pow(0.93, years)
  const lalRemaining = initialLAL * remainingFraction

  // Volume at 46% ABV = LAL / 0.46
  // Number of 700ml bottles = (LAL / 0.46) / 0.7
  // Simplified: bottles = LAL / (0.46 * 0.7) = LAL / 0.322
  const bottles = lalRemaining / (0.46 * 0.7)

  return Math.floor(bottles).toString()
}

// Google Analytics tracking watchers
// Track filter changes
watch(() => filters.value.status, (newStatus, oldStatus) => {
  if (oldStatus && JSON.stringify(newStatus) !== JSON.stringify(oldStatus)) {
    trackEvent('rackhouse_filter_status', {
      selected_statuses: newStatus.join(','),
      count: newStatus.length
    })
  }
}, { deep: true })

watch(() => filters.value.ageMin, (newAge, oldAge) => {
  if (oldAge !== undefined && newAge !== oldAge) {
    trackEvent('rackhouse_filter_age', {
      min_age: newAge
    })
  }
})

watch(() => filters.value.abv, (newAbv, oldAbv) => {
  if (oldAbv !== undefined && newAbv !== oldAbv) {
    trackEvent('rackhouse_filter_abv', {
      min_abv: newAbv
    })
  }
})

watch(() => filters.value.barrelType, (newType, oldType) => {
  if (oldType !== undefined && newType !== oldType) {
    trackEvent('rackhouse_filter_barrel_type', {
      barrel_type: newType || 'all'
    })
  }
})

watch(() => filters.value.content, (newContent, oldContent) => {
  if (oldContent !== undefined && newContent !== oldContent) {
    trackEvent('rackhouse_filter_content', {
      content_type: newContent || 'all'
    })
  }
})

watch(() => filters.value.batch, (newBatch, oldBatch) => {
  if (oldBatch !== undefined && newBatch !== oldBatch) {
    trackEvent('rackhouse_filter_batch', {
      batch_number: newBatch || 'all'
    })

    // Update URL query parameter for shareability
    const query = { ...route.query }
    if (newBatch) {
      query.batch = newBatch
    } else {
      delete query.batch
    }
    router.replace({ query })
  }
})

// Track sort changes
watch(sortBy, (newSort, oldSort) => {
  if (oldSort !== undefined && newSort !== oldSort) {
    trackEvent('rackhouse_sort_change', {
      sort_by: newSort || 'barrel_number'
    })
  }
})

// Track filter panel toggle
watch(showFilters, (isOpen) => {
  trackEvent('rackhouse_filters_toggle', {
    action: isOpen ? 'open' : 'close'
  })
})

// Fetch barrel SVG on mount
onMounted(async () => {
  // Fetch barrels data
  await fetchBarrels()

  // Track rackhouse page view with data
  if (!error.value && barrels.value.length > 0) {
    trackEvent('rackhouse_page_view', {
      total_barrels: barrels.value.length,
      filled_barrels: filledCasksCount.value,
      median_color: medianColor.value
    })
  }

  // Fetch barrel SVG
  try {
    const response = await fetch('/image/83Barrel.svg')
    let svgText = await response.text()

    // Remove the opening <svg> tag and closing </svg> to get just the content
    svgText = svgText.replace(/<\?xml[^>]*>/, '')
    svgText = svgText.replace(/<!DOCTYPE[^>]*>/, '')

    barrelSvgContent.value = svgText
  } catch (error) {
    console.error('Failed to load barrel SVG:', error)
  }

  // Add window resize listener
  window.addEventListener('resize', handleResize)

  // Check if we have a batch filter in the URL query
  const batchParam = route.query.batch
  if (batchParam) {
    // Check if this batch exists in available batches
    if (availableBatches.value.includes(batchParam)) {
      filters.value.batch = batchParam

      trackEvent('rackhouse_batch_url', {
        batch_number: batchParam
      })

      // Scroll to the barrel grid after a short delay
      setTimeout(() => {
        const barrelGrid = document.querySelector('.barrel-grid')
        if (barrelGrid) {
          const headerOffset = 120
          const elementPosition = barrelGrid.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }

  // Check if we have a barrel number in the URL
  const barrelNumberParam = route.params.barrelNumber
  if (barrelNumberParam) {
    const barrelNumber = parseInt(barrelNumberParam, 10)
    if (!isNaN(barrelNumber)) {
      // Wait a bit for the grid to render
      setTimeout(() => {
        scrollToBarrel(barrelNumber)
      }, 300)
    }
  }
})

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.text-gold {
  color: #F6D993;
}

/* Ensure SVG fills the container */
.barrel-svg :deep(svg) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Selected barrel highlight */
.barrel-selected {
  transform: scale(1.1);
  z-index: 10;
}

/* Barrel details panel */
.barrel-details-panel {
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

/* Slide down animation */
.slide-down-enter-active {
  animation: slideDown 0.4s ease-out;
}

.slide-down-leave-active {
  animation: slideDown 0.3s ease-in reverse;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
    max-height: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    max-height: 500px;
  }
}

/* Custom grid columns with responsive breakpoints - max 10 columns */
.grid-cols-custom {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

@media (min-width: 640px) {
  .grid-cols-custom {
    grid-template-columns: repeat(10, minmax(0, 1fr));
  }
}
</style>
